// A form field's widget on the page (M8, AC-ACRO-3/4): an input of the field's kind over the widget's rectangle.
// The page's own drawing of the field shows through (the input is transparent until it has the focus). Text is
// written when the field loses the focus or Enter is pressed (only if it changed); buttons and choices at once.
// Fields FlexPDF does not fill in say why. A signature field takes a picture (D-65, AC-SIG-1).
import { useLayoutEffect, useRef, useState } from 'react';
import type { FieldWidget } from '../../engine/worker/protocol.ts';

export interface FieldBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

type Value = string | string[] | boolean;

/** A radio button's export value: through /Opt when its on-state is an index into it. */
export const exportOf = (f: FieldWidget): string | undefined => (f.options && f.onState !== undefined && /^\d+$/.test(f.onState) ? f.options[Number(f.onState)]?.[0] : f.onState);

const testId = (page: number, f: FieldWidget) => `field-${page}-${f.id.replace(/ 0 R$/, '').replace(/ /g, '-')}`;

/**
 * The pages are built again for every version of the document, so a field that had the focus (or was Tabbed to while
 * the previous one was being written) gets it back when it appears again.
 */
let focusNext: string | undefined;

export function FieldArea(props: { page: number; field: FieldWidget; box: FieldBox; disabled: boolean; onFill: (f: FieldWidget, value: Value) => void; onSign: (f: FieldWidget, file: File) => void }) {
  const { field: f, box: b } = props;
  const id = testId(props.page, f);
  const el = useRef<HTMLElement>(null);
  const initial = typeof f.value === 'string' ? f.value : '';
  const [draft, setDraft] = useState(initial);
  /** Escape was pressed: the blur that follows must not write the text typed (the draft is still the old one then) */
  const cancelled = useRef(false);
  // A new value from outside (undo, redo, another widget of the field): shown, unless the user is typing here.
  useLayoutEffect(() => {
    if (document.activeElement !== el.current) setDraft(initial);
  }, [initial]);
  // Back from a change (the fields are disabled meanwhile, or built again): the field that should have the focus gets it.
  useLayoutEffect(() => {
    if (!props.disabled && focusNext === id) {
      focusNext = undefined;
      el.current?.focus();
    }
  }, [props.disabled]);
  const label = `${f.name}${f.required ? ' (required)' : ''}`;
  const readOnly = !!f.locked;
  /** Writes `value`; the field that gets the focus next (if any) keeps it across the new version. */
  const commit = (value: Value, next?: EventTarget | null) => {
    focusNext = next instanceof HTMLElement ? (next.dataset.testid ?? undefined) : id;
    props.onFill(f, value);
  };
  const common = {
    'data-testid': id,
    'data-name': f.name,
    'data-kind': f.kind,
    'data-locked': f.locked,
    'aria-label': label,
    title: f.locked ? `${label}: cannot be filled in (${f.locked})` : label,
    className: f.locked ? 'field is-locked' : 'field',
    style: { left: b.left, top: b.top, width: b.width, height: b.height },
  };
  // A signature field: a click (or Enter) chooses a picture to put in it; it is not a digital signature.
  if (f.kind === 'signature' && !f.pictureLocked) {
    const input = (
      <input
        data-testid={`picture-${id}`}
        type="file"
        accept="image/*"
        hidden
        disabled={props.disabled}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) props.onSign(f, file);
        }}
      />
    );
    return (
      <label
        {...common}
        data-locked={undefined}
        className="field field-sign"
        title={`${label}: put a picture of your signature in it (PNG, JPEG or another picture). It is a picture, not a digital signature.`}
        tabIndex={props.disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          e.currentTarget.querySelector('input')?.click();
        }}
      >
        {input}
        <span className="field-sign-hint">Signature picture</span>
      </label>
    );
  }
  // Never offered: an area that says why, not an input (it cannot get the focus).
  if (f.locked && (f.kind === 'button' || f.kind === 'signature')) return <div {...common} role="note" />;
  switch (f.kind) {
    case 'checkbox':
    case 'radio': {
      const on = f.kind === 'radio' ? f.value === exportOf(f) : f.value === f.onState;
      return (
        <input
          {...common}
          ref={el as React.RefObject<HTMLInputElement>}
          type={f.kind}
          name={f.kind === 'radio' ? `field-${props.page}-${f.field}` : undefined}
          checked={on}
          aria-readonly={readOnly}
          disabled={props.disabled}
          onChange={(e) => !readOnly && (f.kind === 'checkbox' ? commit(e.target.checked) : e.target.checked && commit(true))}
          onClick={(e) => readOnly && e.preventDefault()}
        />
      );
    }
    case 'combo':
    case 'list': {
      const chosen = Array.isArray(f.value) ? f.value : f.value !== undefined ? [f.value] : [];
      const options = f.options ?? [];
      // An editable combo box's own text (not one of its options) is shown as an option too.
      const extra = chosen.filter((v) => !options.some(([ex]) => ex === v)).map((v): [string, string] => [v, v]);
      const multi = f.kind === 'list' && !!f.multiSelect;
      // A list's (invisible) rows as tall as the drawn ones (FlexPDF draws a row (size × 1.15) pt high), so a click
      // chooses the row under the pointer.
      const turned = f.rotation === 90 || f.rotation === 270;
      const scale = b.height / ((turned ? f.rect[2] - f.rect[0] : f.rect[3] - f.rect[1]) || 1);
      const row = (f.font?.size || 12) * 1.15 * scale;
      return (
        <select
          {...common}
          ref={el as React.RefObject<HTMLSelectElement>}
          multiple={multi}
          size={f.kind === 'list' ? Math.max(2, options.length) : undefined}
          style={f.kind === 'list' ? { ...common.style, ['--row' as string]: `${row}px` } : common.style}
          value={multi ? chosen : (chosen[0] ?? '')}
          aria-readonly={readOnly}
          disabled={props.disabled || readOnly}
          onChange={(e) => {
            const picked = [...e.target.selectedOptions].map((o) => o.value);
            commit(f.kind === 'list' ? picked : picked[0]);
          }}
        >
          {[...extra, ...options].map(([ex, shown]) => (
            <option key={ex} value={ex}>
              {shown}
            </option>
          ))}
        </select>
      );
    }
    default: {
      const text = {
        ...common,
        value: draft,
        readOnly,
        disabled: props.disabled,
        spellCheck: false,
        style: { ...common.style, fontSize: Math.max(8, Math.min(16, f.multiline ? 13 : b.height * 0.62)) },
        onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDraft(e.target.value),
        onBlur: (e: React.FocusEvent) => {
          if (cancelled.current) cancelled.current = false;
          else if (!readOnly && draft !== initial) commit(draft, e.relatedTarget);
        },
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Escape') {
            cancelled.current = true;
            setDraft(initial);
            (e.target as HTMLElement).blur();
          } else if (e.key === 'Enter' && !f.multiline && !readOnly) {
            e.preventDefault();
            if (draft !== initial) commit(draft);
          }
        },
      };
      if (f.multiline) return <textarea {...text} ref={el as React.RefObject<HTMLTextAreaElement>} />;
      return <input {...text} ref={el as React.RefObject<HTMLInputElement>} type={f.password ? 'password' : 'text'} maxLength={f.maxLen} />;
    }
  }
}
